"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { alumni, type Alumnus } from "@/data/alumniData";
import {
  logoSrc,
  logoSrcSet,
  resolveLogoHeight,
  LOGO_IMG_CLASS,
  LOGO_SLOT_CLASS,
  type LogoSizing,
} from "@/components/newPageComponent/CommonSection/companyLogo";
import useRevealOnScroll from "./useRevealOnScroll";
const slides = alumni.length < 4 ? [...alumni, ...alumni] : alumni;
const INITIAL_VISIBLE = 9;
const REVEAL_BATCH = 6;

const ik = (url: string, transform: string) =>
  `${url}${url.includes("?") ? "&" : "?"}tr=${transform}`;

const AVATAR_CROP = "fo-top";

/** Avatars are square: 100 covers 1x, 200 a 2x phone, 300 a 3x one. */
const avatarSrc = (url: string, size: number) =>
  ik(url, `f-webp,q-70,w-${size},h-${size},${AVATAR_CROP}`);

const avatarSrcSet = (url: string) =>
  [100, 200, 300]
    .map((width) => `${avatarSrc(url, width)} ${width}w`)
    .join(", ");

/** Shape per mark, measured from each asset's trimmed aspect ratio, keyed by
    filename because seven records share the KPMG mark and six share Wipro.
    Anything absent falls back to "wide". The handful of explicit heights match
    what the same brand already uses on AboutCPA, so a logo is the same size
    wherever it appears. This set is cropped differently from the CPA and CMA
    one — Wipfli is 3.1:1 here against 6.6:1 there — so the shapes are measured
    from these assets, not copied across. */
const LOGO_SIZING: Record<string, LogoSizing> = {
  "BDO.png": { logoShape: "compact" },
  "Genpact.png": { logoShape: "compact" },
  "Goldman%20Sachs.png": { logoShape: "compact" },
  "HPE.png": { logoShape: "compact" },
  "IBM.png": { logoShape: "compact" },
  "KPMG.png": { logoShape: "compact", logoHeight: 27 },
  "Premier%20Research.png": { logoShape: "compact" },
  "Wiphli.png": { logoShape: "compact" },
  "Wipro.png": { logoShape: "compact" },
  "EY.svg": { logoShape: "stacked" },
  "PWC.png": { logoShape: "stacked" },
  "SS&C%20GlobeOp.png": { logoShape: "stacked" },
  "Unison%20Globus.png": { logoShape: "stacked", logoHeight: 42 },
  "Warner%20brothers.png": { logoShape: "stacked" },
  "DXC.png": { logoHeight: 20 },
  "Delottie%20.png": { logoHeight: 20 },
  "ZENI.png": { logoHeight: 20 },
};

const logoHeightFor = (url: string) =>
  resolveLogoHeight(LOGO_SIZING[url.split("/company-logos/")[1] ?? ""] ?? {});

const NavButton = ({
  direction,
  onClick,
}: {
  direction: "prev" | "next";
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={direction === "prev" ? "Previous alumni" : "Next alumni"}
    className={`hidden md:flex absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full border border-[#7A7A7A] text-white hover:border-white transition-colors ${
      direction === "prev" ? "left-0" : "right-0"
    }`}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      {direction === "prev" ? (
        <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
      ) : (
        <path d="M5 12h14m0 0-6-6m6 6-6 6" />
      )}
    </svg>
  </button>
);

const AlumniCard = ({ alumnus }: { alumnus: Alumnus }) => {
  const logoHeight = alumnus.logo ? logoHeightFor(alumnus.logo) : 0;

  return (
    <div className="h-full flex flex-col items-center text-center bg-[#141414] rounded-2xl px-4 py-4 md:py-4">
      {/* width/height carry the intrinsic ratio, so nothing reflows as these
        stream in one by one while the track is being scrolled. */}
      <img
        src={avatarSrc(alumnus.image, 200)}
        srcSet={avatarSrcSet(alumnus.image)}
        sizes="(max-width: 768px) 85px, 100px"
        alt={`${alumnus.name}, Miles alumni at ${alumnus.company}`}
        width={100}
        height={100}
        loading="lazy"
        decoding="async"
        className="w-[85px] h-[85px] md:w-[100px] md:h-[100px] rounded-full object-cover"
      />

      <h3 className="text-white text-base md:text-[20px] font-bold mt-4">
        {alumnus.name}
      </h3>
      {alumnus.designation && (
        <p className="text-white text-sm md:text-base font-semibold mt-0.5">
          {alumnus.designation}
        </p>
      )}

      {/* Employers without a mark in the asset sheet get their name set in type
        in the same slot, so the row below it stays on one line. */}
      <span className={LOGO_SLOT_CLASS}>
        {alumnus.logo ? (
          <img
            src={logoSrc(alumnus.logo, logoHeight)}
            srcSet={logoSrcSet(alumnus.logo, logoHeight)}
            alt={alumnus.company}
            loading="lazy"
            decoding="async"
            style={{ maxHeight: logoHeight }}
            className={LOGO_IMG_CLASS}
          />
        ) : (
          <span className="text-sm font-bold text-white">
            {alumnus.company}
          </span>
        )}
      </span>

      {/* Clamped because the quotes run from one line to four sentences, and
        items-stretch means the longest one would set the height of all 39. */}
      <p className="text-white text-xs md:text-sm leading-[22px] mt-4 line-clamp-5">
        {alumnus.quote}
      </p>

      <div className="flex flex-wrap justify-center gap-2 mt-auto pt-5">
        {alumnus.scores.map((score) => (
          <span
            key={score}
            className="bg-white text-[#333] text-[10px] font-bold rounded-full px-2.5 py-1"
          >
            {score}
          </span>
        ))}
      </div>
    </div>
  );
};

const CpaCmaAlumni: React.FC = () => {
  const { trackRef, visibleCount, fullyRevealed, revealMore } =
    useRevealOnScroll(slides.length, {
      initial: INITIAL_VISIBLE,
      batch: REVEAL_BATCH,
    });

  /** Where the last click asked the track to land. A smooth scroll takes a few
      hundred ms, so scrollLeft read during one reports a half-finished offset —
      stepping from that lands between cards, and every later click inherits the
      error. Desktop has no snap to pull it back (md:snap-none), which is why
      the grid only breaks there. Null means nothing is in flight, so the live
      position is the honest one. */
  const targetRef = useRef<number | null>(null);

  /** Clear the pending target once scrolling stops, whether that was our own
      animation finishing or the user dragging the track themselves. */
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let settle = 0;
    const onScroll = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        targetRef.current = null;
      }, 120);
    };

    track.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      track.removeEventListener("scroll", onScroll);
      window.clearTimeout(settle);
    };
  }, [trackRef]);

  const scrollByCard = useCallback(
    (direction: 1 | -1) => {
      const track = trackRef.current;
      if (!track) return;

      const maxScroll = track.scrollWidth - track.clientWidth;
      if (maxScroll <= 0) return;

      const card = track.firstElementChild as HTMLElement | null;
      // getBoundingClientRect, not offsetWidth: a third of the track is 341.33px
      // as often as not, and the rounded integer would drift a pixel a card.
      const step = card
        ? card.getBoundingClientRect().width
        : track.clientWidth / 3;
      if (step <= 0) return;

      const base = targetRef.current ?? track.scrollLeft;
      // Rounding re-seats the grid if a drag left the track between cards.
      let next = Math.round(base / step) * step + direction * step;

      // 1px of tolerance for sub-pixel scroll positions.
      if (next > maxScroll + 1) {
        if (fullyRevealed) {
          next = 0;
        } else {
          revealMore();
          next = maxScroll;
        }
      } else if (next < 0) {
        next = maxScroll;
      } else {
        // Never park past the end, or the next click would step from a
        // position the track could not actually reach.
        next = Math.min(next, maxScroll);
      }

      targetRef.current = next;
      track.scrollTo({ left: next, behavior: "smooth" });
    },
    [fullyRevealed, revealMore, trackRef],
  );

  const scrollPrev = useCallback(() => scrollByCard(-1), [scrollByCard]);
  const scrollNext = useCallback(() => scrollByCard(1), [scrollByCard]);

  return (
    <section className="bg-black w-full pt-[50px] pb-6 md:py-[50px]">
      <div className="text-center px-4 md:px-8">
        <h2 className="text-white text-[18px] md:text-[24px] font-bold leading-normal">
          Success Stories
        </h2>
        {/* DRAFT COPY — needs sign-off. Uses the alumni figure already claimed
            in the page metadata. */}
      </div>

      {/* Gutter on an outer wrapper so the absolutely placed arrows sit on the
          page's content edge rather than the screen edge. */}
      <div className="mt-6 md:mt-8 xl:px-[120px] min-[1700px]:px-[170px]">
      <div className="relative md:px-16">
        <NavButton direction="prev" onClick={scrollPrev} />
        <div
          ref={trackRef}
          className="scrollbar-hide flex items-stretch snap-x snap-mandatory overflow-x-auto overflow-y-hidden overscroll-x-contain pl-2 scroll-pl-2 md:snap-none md:pl-0 md:scroll-pl-0"
        >
          {slides.slice(0, visibleCount).map((alumnus, index) => (
            <div
              key={`${alumnus.id}-${index}`}
              className="w-[82%] md:w-[33.333%] shrink-0 snap-start px-2 md:px-4"
            >
              <AlumniCard alumnus={alumnus} />
            </div>
          ))}
        </div>

        <NavButton direction="next" onClick={scrollNext} />
      </div>
      </div>
    </section>
  );
};

export default CpaCmaAlumni;
